import { useState, useEffect, useMemo, useRef } from 'react';
import { SellOnlineSwitch, useStoreFeature } from '../components/commerce/SellOnline';
import { Link } from 'react-router-dom';
import { useUserContext } from '../contexts/UserContext';
import { useBranch } from '../contexts/BranchContext';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import EmptyState from '../components/ui/EmptyState';
import AddPlanModal from '../components/AddPlanModal';
import PlanManagementPanel from '../components/PlanManagementPanel';
import {
  Search,
  SlidersHorizontal,
  Plus,
  X,
  Filter,
  ChevronRight,
  LayoutGrid,
  Zap,
  Users,
  CreditCard,
  Trash2
} from 'lucide-react';

interface MembershipPlan {
  id: string;
  sellOnline?: boolean;
  name: string;
  description: string | null;
  durationMonths: number | null;
  price: number | null;
  isActive: boolean;
  isDefault: boolean;
  features?: any;
  createdAt: string;
  updatedAt: string;
  gym: {
    id: string;
    name: string;
    displayName: string;
  };
  branchMappings: Array<{
    branch: {
      id: string;
      name: string;
      displayName: string;
    };
  }>;
  _count: {
    memberships: number;
  };
}

export default function PlansPage() {
  const { hasPermission } = useUserContext();
  const { selectedBranchId } = useBranch();
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const { storeFeature } = useStoreFeature();
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPlanForManagement, setSelectedPlanForManagement] = useState<MembershipPlan | null>(null);

  // Filter and sort state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [sortBy, setSortBy] = useState<'name' | 'price' | 'members' | 'created'>('name');

  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const filterDrawerRef = useRef<HTMLDivElement>(null);
  const [draftStatusFilter, setDraftStatusFilter] = useState(statusFilter);
  const [draftSortBy, setDraftSortBy] = useState(sortBy);

  const canManagePlans = hasPermission('manage_plans');

  // Sync draft states
  useEffect(() => {
    if (showFiltersDrawer) {
      setDraftStatusFilter(statusFilter);
      setDraftSortBy(sortBy);
    }
  }, [showFiltersDrawer, statusFilter, sortBy]);

  // Handle click outside drawer
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (filterDrawerRef.current && !filterDrawerRef.current.contains(event.target as Node)) {
        setShowFiltersDrawer(false);
      }
    }
    if (showFiltersDrawer) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFiltersDrawer]);

  // Fetch plans
  const fetchPlans = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No access token');

      const branchQuery = selectedBranchId ? `?branchId=${selectedBranchId}` : '';
      const response = await fetch(`${API_BASE_URL}/api/member-plans${branchQuery}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPlans(data.data || []);
      } else {
        throw new Error('Failed to fetch plans');
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load membership plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canManagePlans) fetchPlans();
  }, [canManagePlans, selectedBranchId]);

  // Handle search submit
  const handleSearch = () => {
    setSearchTerm(searchInput);
  };

  const filteredPlans = useMemo(() => {
    let filtered = plans;

    if (statusFilter === 'active') {
      filtered = filtered.filter(plan => plan.isActive);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(plan => !plan.isActive);
    }

    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(plan =>
        plan.name.toLowerCase().includes(query) ||
        plan.description?.toLowerCase().includes(query)
      );
    }

    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name': return a.name.localeCompare(b.name);
        case 'price': return (a.price || 0) - (b.price || 0);
        case 'members': return b._count.memberships - a._count.memberships;
        case 'created': return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        default: return 0;
      }
    });
  }, [plans, statusFilter, searchTerm, sortBy]);

  const activeFilterCount = (statusFilter !== 'all' ? 1 : 0) + (sortBy !== 'name' ? 1 : 0);

  const handleDeletePlan = async (plan: MembershipPlan) => {
    if (!confirm(`Are you sure you want to delete "${plan.name}"?`)) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${API_BASE_URL}/api/member-plans/${plan.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Plan deleted!');
        fetchPlans();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Delete failed');
      }
    } catch (error) {
      toast.error('Error deleting plan');
    }
  };

  if (!canManagePlans) {
    return <EmptyState title="Access Denied" description="No permission to manage plans." variant="error" />;
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Premium Header */}
      <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50/50 rounded-full blur-3xl -mr-32 -mt-32 transition-colors group-hover:bg-blue-100/50"></div>
        <div className="relative">
          <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none uppercase">Membership Plans</h1>
          <p className="text-[10px] font-black text-gray-400 mt-2 uppercase tracking-[0.2em]">Inventory & Pricing Control</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 relative">
          <Link
            to="/plans/preview"
            className="px-4 py-2.5 bg-gray-50 hover:bg-white text-gray-700 rounded-xl font-black text-[10px] sm:text-xs uppercase tracking-widest transition-all border border-gray-100 hover:border-blue-200 active:scale-95 shadow-sm flex items-center justify-center gap-2"
          >
            <Zap className="h-3.5 w-3.5 text-blue-600" />
            <span>Public Preview</span>
          </Link>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black text-[10px] sm:text-xs uppercase tracking-widest transition-all active:scale-95 shadow-lg shadow-blue-100 flex items-center justify-center gap-2"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Add New Plan</span>
          </button>
        </div>
      </div>

      {/* Modern Filter Row */}
      <div className="flex items-center justify-between gap-2 bg-white p-2 rounded-2xl shadow-sm border border-gray-100">
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setSearchTerm(e.target.value);
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search by plan name or description..."
            className="block w-full pl-9 pr-10 py-2.5 border border-transparent rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all shadow-inner"
          />
          {searchInput && (
            <button
              onClick={() => {
                setSearchInput('');
                setSearchTerm('');
              }}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors group"
            >
              <X className="h-4 w-4 group-hover:scale-110 transition-transform" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleSearch}
            className="hidden sm:flex px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-xl text-sm font-black transition-all border border-gray-100 active:scale-95"
          >
            Search
          </button>
          <button
            onClick={() => setShowFiltersDrawer(true)}
            className={`relative p-2.5 rounded-xl transition-all active:scale-95 border shadow-sm flex items-center gap-2 ${activeFilterCount > 0
              ? 'bg-blue-50 border-blue-200 text-blue-600'
              : 'bg-gray-50 border-gray-100 text-gray-700 hover:bg-gray-100'
              }`}
          >
            <SlidersHorizontal className="w-5 h-5" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white ring-2 ring-white">
                {activeFilterCount}
              </span>
            )}
            <span className="hidden md:inline text-sm font-black uppercase tracking-wider">Filters</span>
          </button>
        </div>
      </div>

      {/* Filter Drawer */}
      {showFiltersDrawer && (
        <div className="fixed inset-0 z-[1000] flex justify-end overflow-hidden bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div
            ref={filterDrawerRef}
            className="w-full max-w-[320px] bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300"
          >
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
                <Filter className="w-5 h-5 text-blue-600" />
                Filters
              </h3>
              <button onClick={() => setShowFiltersDrawer(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8">
              <div className="space-y-4">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Plan Status</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { val: 'all', label: 'All Plans' },
                    { val: 'active', label: 'Active' },
                    { val: 'inactive', label: 'Inactive' }
                  ].map(({ val, label }) => (
                    <button
                      key={val}
                      onClick={() => setDraftStatusFilter(val as any)}
                      className={`px-4 py-2 rounded-xl text-xs font-black transition-all border ${draftStatusFilter === val
                        ? 'bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-200'
                        : 'bg-white border-gray-100 text-gray-600 hover:border-blue-200 hover:text-blue-600'
                        }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Sort By</label>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { val: 'name', label: 'Alpha (Name)', icon: <LayoutGrid className="w-3.5 h-3.5" /> },
                    { val: 'price', label: 'Base Price', icon: <CreditCard className="w-3.5 h-3.5" /> },
                    { val: 'members', label: 'Popularity', icon: <Users className="w-3.5 h-3.5" /> },
                    { val: 'created', label: 'Recency', icon: <Zap className="w-3.5 h-3.5" /> }
                  ].map(({ val, label, icon }) => (
                    <button
                      key={val}
                      onClick={() => setDraftSortBy(val as any)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-black transition-all border ${draftSortBy === val
                        ? 'bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-200'
                        : 'bg-white border-gray-100 text-gray-600 hover:border-blue-200 hover:text-blue-600'
                        }`}
                    >
                      {icon}
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex gap-3">
              <button
                onClick={() => {
                  setStatusFilter('active');
                  setSortBy('name');
                  setShowFiltersDrawer(false);
                }}
                className="flex-1 px-4 py-3 bg-white border border-gray-200 text-gray-600 rounded-xl text-xs font-black hover:bg-gray-50 transition-all active:scale-95"
              >
                Reset
              </button>
              <button
                onClick={() => {
                  setStatusFilter(draftStatusFilter);
                  setSortBy(draftSortBy as any);
                  setShowFiltersDrawer(false);
                }}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl text-xs font-black hover:bg-blue-700 transition-all active:scale-95 shadow-lg shadow-blue-100"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plans List */}
      {filteredPlans.length === 0 ? (
        <EmptyState
          icon="💳"
          title="Zero plans found"
          description="Try adjusting your filters or create a new plan."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredPlans.map((plan) => (
            <div
              key={plan.id}
              onClick={() => setSelectedPlanForManagement(plan)}
              className="bg-white rounded-[2rem] shadow-soft border border-gray-100 p-5 transition-all hover:border-blue-200 group flex flex-col sm:flex-row sm:items-center gap-4 cursor-pointer sm:cursor-default relative overflow-hidden"
            >
              <div className="flex items-center gap-5 flex-1 min-w-0">
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 font-black text-2xl flex-shrink-0 shadow-inner group-hover:bg-blue-100 transition-colors">
                  {plan.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-black text-gray-900 truncate tracking-tight">{plan.name}</h3>
                        {plan.isDefault && (
                          <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest bg-blue-50 text-blue-600 border border-blue-100">Default</span>
                        )}
                        {storeFeature && (
                          <SellOnlineSwitch compact endpoint={`/api/member-plans/${plan.id}`} sellOnline={!!plan.sellOnline}
                            onChanged={v => setPlans(list => list.map(x => x.id === plan.id ? { ...x, sellOnline: v } : x))} />
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                        <span className="text-sm font-black text-blue-600">₹{plan.price || 0}</span>
                        <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{plan.durationMonths || 0} Months</span>
                      </div>
                      <div className="text-[10px] font-bold text-gray-500 mt-1 flex items-center gap-1.5">
                        <Users className="w-3 h-3" />
                        {plan._count.memberships} Active memberships
                      </div>
                    </div>
                    {/* Delete button for Mobile - absolute positioned for zero row impact */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePlan(plan);
                      }}
                      className="sm:hidden -mr-2 -mt-2 p-2.5 text-gray-300 hover:text-red-500 active:scale-90 transition-all"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Actions for Desktop */}
              <div className="hidden sm:flex items-center gap-3 shrink-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPlanForManagement(plan);
                  }}
                  className="px-6 py-3 bg-gray-50 hover:bg-blue-600 hover:text-white text-gray-700 rounded-xl text-xs font-black uppercase tracking-widest transition-all active:scale-95 border border-gray-100 hover:border-blue-600 flex items-center justify-center gap-2"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Manage
                </button>
                <div className="h-8 w-px bg-gray-100"></div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeletePlan(plan);
                  }}
                  className="p-3 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all active:scale-90"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <ChevronRight className="w-5 h-5 text-gray-200" />
              </div>
            </div>
          ))}
        </div>
      )}

      <AddPlanModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onSuccess={fetchPlans} />
      {selectedPlanForManagement && (
        <PlanManagementPanel
          plan={selectedPlanForManagement}
          onClose={() => setSelectedPlanForManagement(null)}
          onUpdate={() => { fetchPlans(); setSelectedPlanForManagement(null); }}
        />
      )}
    </div>
  );
}
